import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  Clock, MapPin, TrendingUp, Database,
  Building2, Radio, Activity, Eye,
  ArrowRight, Star, Quote, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const USAPage = () => {
  const { t, language } = useLanguage();
  const [counters, setCounters] = useState({ uptime: 0, latency: 0, speed: 0, customers: 0 });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Animated Counters
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounters({
        uptime: Math.min(99.9, (99.9 * progress)).toFixed(1),
        latency: Math.min(5, (5 * progress)).toFixed(1),
        speed: Math.min(1, (1 * progress)).toFixed(1),
        customers: Math.min(5000, Math.floor(5000 * progress))
      });

      if (step >= steps) clearInterval(timer);
    }, interval);
  };

  // Testimonials Carousel
  useEffect(() => {
    const testimonials = t('usa.testimonials.items');
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [t]);

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  const testimonials = t('usa.testimonials.items');

  return (
    <div className="min-h-screen bg-white">
      <SEO page="usa" />
      <Header />
      
      {/* Hero Section - American Blue with Animated Gradient */}
      <section className="relative min-h-screen flex items-center overflow-hidden py-20 sm:py-24 md:py-32">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600 via-transparent to-red-600 animate-pulse"></div>
          </div>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4" />
              <span>{t('usa.hero.badge')}</span>
            </div>
            
            <h1 className="text-7xl md:text-8xl font-black mb-6 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-white">
              {t('usa.hero.title')}
            </h1>
            
            <p className="text-2xl md:text-3xl text-blue-100 mb-8 max-w-4xl mx-auto font-light">
              {t('usa.hero.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Button 
                onClick={scrollToPricing}
                className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-10 py-8 text-xl rounded-full shadow-2xl"
              >
                {t('usa.hero.getStarted')}
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
              <Button 
                onClick={scrollToPricing}
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-10 py-8 text-xl rounded-full"
              >
                {t('usa.hero.viewPlans')}
              </Button>
            </div>

            {/* Animated Stats */}
            <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2">{counters.uptime}%</div>
                <div className="text-xs sm:text-sm text-blue-200">{t('usa.stats.uptimeLabel')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2">{counters.latency}ms</div>
                <div className="text-xs sm:text-sm text-blue-200">{t('usa.stats.latencyLabel')}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2">{counters.speed}Gbps</div>
                <div className="text-xs sm:text-sm text-blue-200">{t('usa.stats.speedLabel')}</div>
                <Link
                  to={(language === 'ru' ? '/ru' : '') + '/wiki/shared-1gbps-port-explained'}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] text-blue-200/80 hover:text-white transition-colors"
                >
                  {language === 'ru' ? 'Что это значит?' : 'What does this mean?'}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-6">
                <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-2">{counters.customers.toLocaleString()}+</div>
                <div className="text-xs sm:text-sm text-blue-200">{t('usa.stats.customersLabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Grid Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('usa.bento.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              {t('usa.bento.subtitle')}
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Large Feature */}
            <div className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <Server className="w-16 h-16 mb-6" />
                <h3 className="text-3xl font-bold mb-4">{t('usa.bento.items.0.title')}</h3>
                <p className="text-lg text-blue-100 mb-6">{t('usa.bento.items.0.desc')}</p>
                <img src="/images/locations/unsplash_1549041050-386c1c99d655.jpg" 
                  alt="LA at Night" loading="lazy"
                  className="rounded-2xl w-full h-64 object-cover shadow-2xl"
 loading="lazy"/>
              </div>
            </div>

            {/* Small Feature 1 */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100">
              <Zap className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{t('usa.bento.items.1.title')}</h3>
              <p className="text-sm sm:text-base text-gray-600">{t('usa.bento.items.1.desc')}</p>
            </div>

            {/* Small Feature 2 */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100">
              <Shield className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{t('usa.bento.items.2.title')}</h3>
              <p className="text-sm sm:text-base text-gray-600">{t('usa.bento.items.2.desc')}</p>
            </div>

            {/* Medium Feature with Image */}
            <div className="md:col-span-2 bg-gradient-to-br from-gray-900 to-blue-900 rounded-3xl overflow-hidden">
              <img src="/images/locations/unsplash_1619678562883-7f77b7c68d3c.jpg" 
                alt="Los Angeles Skyline" loading="lazy"
                className="w-full h-48 sm:h-64 object-cover"
 loading="lazy"/>
              <div className="p-6 sm:p-8 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">{t('usa.bento.items.3.title')}</h3>
                <p className="text-sm sm:text-base text-blue-100">{t('usa.bento.items.3.desc')}</p>
              </div>
            </div>

            {/* Small Feature 3 */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100">
              <Network className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{t('usa.bento.items.4.title')}</h3>
              <p className="text-sm sm:text-base text-gray-600">{t('usa.bento.items.4.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related VPS Locations */}
      <RelatedLocations currentLocation="vps-usa" />

      {/* Pricing - High Position */}
      <div id="pricing">
        <PricingSection 
          hideHeader={false}
          title={t('usa.pricing.title')}
          subtitle={t('usa.pricing.subtitle')}
        />
      </div>

      <PaymentMethodsSection transparentBg={false} />

      {/* Timeline Infrastructure */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-4">
              {t('usa.timeline.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('usa.timeline.subtitle')}
            </p>
          </div>

          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-600 via-blue-400 to-blue-600"></div>

            {/* Timeline Items */}
            {t('usa.timeline.items').map((item, index) => {
              const IconComponent = [Building2, Radio, Shield, Activity][index];
              const isLeft = index % 2 === 0;
              
              return (
                <div key={index} className={`relative flex items-center mb-16 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-5/12 ${isLeft ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-blue-100">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                  
                  <div className="w-2/12 flex justify-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="w-5/12"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black text-gray-900 mb-4">
              {t('usa.testimonials.title')}
            </h2>
            <div className="flex justify-center gap-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="bg-white rounded-3xl p-12 shadow-2xl border border-gray-100">
              <Quote className="w-16 h-16 text-blue-600 mb-6 opacity-20" />
              <p className="text-2xl text-gray-800 mb-8 leading-relaxed italic">
                &ldquo;{testimonials[currentTestimonial].text}&rdquo;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {testimonials[currentTestimonial].author.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-gray-900">{testimonials[currentTestimonial].author}</div>
                  <div className="text-gray-600">{testimonials[currentTestimonial].role}</div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-blue-600" />
              </button>
              <button
                onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-blue-600" />
              </button>
            </div>

            {/* Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    currentTestimonial === index 
                      ? 'bg-blue-600 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location with Parallax Images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl font-black text-gray-900 mb-6">
                {t('usa.location.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {t('usa.location.description')}
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  {t('usa.location.subtitle')}
                </h3>
                <p className="text-gray-700">{t('usa.location.details')}</p>
              </div>
              <ul className="space-y-4">
                {t('usa.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <img src="/images/locations/pexels_30264517.jpg" 
                  alt="Hollywood Sign" loading="lazy"
                  className="w-full h-80 object-cover"
 loading="lazy"/>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <img src="/images/locations/unsplash_1611728337177-89dfbbdc7a3a.jpg" 
                    alt="LA Skyline" loading="lazy"
                    className="w-full h-48 object-cover"
 loading="lazy"/>
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <img src="/images/locations/320px-Flag_of_the_United_States.svg.png" 
                    alt="USA Flag" loading="lazy"
                    className="w-full h-48 object-cover"
 loading="lazy"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA with Bold Design */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900"></div>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-6xl font-black text-white mb-6">
            {t('usa.cta.title')}
          </h2>
          <p className="text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
            {t('usa.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-16 py-10 text-2xl rounded-full shadow-2xl transform hover:scale-105 transition-all"
          >
            {t('usa.cta.button')}
            <ArrowRight className="w-8 h-8 ml-3" />
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-usa" />
      <Footer />
    </div>
  );
};


